import type { AppRequestRoute } from '@shared/config'
import type { ApiResponse } from '@shared/api'

import { useProfileStore } from '@entities/viewer'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface HelperActionReturn {
  isRunning: Ref<boolean>
  canRun: ComputedRef<boolean>
  run: (routeName: AppRequestRoute, body?: Record<string, unknown>) => Promise<boolean>
}

export const useHelperAction = (): HelperActionReturn => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const profileStore = useProfileStore()

  const isRunning = ref<boolean>(false)
  const canRun = computed<boolean>(() => profileStore.privileges?.includes('manage-settings') ?? false)

  const run = async (routeName: AppRequestRoute, body: Record<string, unknown> = {}): Promise<boolean> => {
    const url = useAdminRoutes(routeName)

    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')

      return false
    }

    isRunning.value = true

    try {
      const { data } = await $axios.post<ApiResponse>(url, body)

      if (!data.success) {
        toast.error(data.message)

        return false
      }

      toast.success(data.message)

      return true
    } catch (err: unknown) {
      toast.error(getAxiosErrorMessage(err))

      return false
    } finally {
      isRunning.value = false
    }
  }

  return { isRunning, canRun, run }
}
